//@ts-nocheck
import { Box } from '@mui/material';
import { XGCheckBox, XGSelect } from '@xg-cl/xg-cl';
import { Fragment } from 'react';
import * as PropTypes from 'prop-types';
import './BookingPricingPlansPanel.css';

/**
 * Functional React component for rendering a custom booking pricing plans panel.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the booking pricing plans panel.
 * @param {Object} [props.data] - The data for booking pricing plans panel.
 * @param {Function} [props.setBookingData] - The function to update booking data.
 * @param {Function} [props.onChangePricingPlan] - The function to handle pricing plan change.
 * @param {boolean} [props.isPassengerRemoving] - The boolean value indicating whether the passenger is removing.
 * @param {Array} [props.checkedPassengers] - The array of checked passengers.
 * @param {Array} [props.tripPricingPlans] - The array of trip pricing plans.
 *
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the booking pricing plans panel component.
 */
const BookingPricingPlansPanel = ({
  id,
  data = {},
  setBookingData = () => {},
  onChangePricingPlan = () => {},
  isPassengerRemoving = false,
  checkedPassengers = [],
  tripPricingPlans = [],
  ...rest
}) => {
  const { bookingplans = [] } = data;
  const checkedPassengerIds = checkedPassengers?.map((passenger) => {
    return passenger?.passengerid;
  });

  const tripPricingPlanItems = tripPricingPlans?.map((tripPricingPlan) => {
    return {
      id: tripPricingPlan?.tripplanid,
      label: tripPricingPlan?.title,
      value: tripPricingPlan?.tripplanid
    };
  });

  const handleChecked = (bookingplanid, passengerid) => {
    const updatedBookingplans = bookingplans?.map((bookingplan) => {
      if (bookingplan?.bookingplanid == bookingplanid) {
        const updatedPassengers = bookingplan?.passengers?.map((passenger) => {
          if (passenger.passengerid == passengerid) {
            return { ...passenger, checked: !passenger.checked };
          }
          return passenger;
        });
        return { ...bookingplan, passengers: updatedPassengers };
      }
      return bookingplan;
    });

    setBookingData({ ...data, bookingplans: updatedBookingplans });
  };

  return (
    <Box className='booking-panel__pricing-plans' id={id} {...rest}>
      {bookingplans?.map((bookingPlan, index) => {
        const { bookingplanid, tripplan, passengers = [] } = bookingPlan;

        return (
          <Fragment key={index}>
            <Box className='booking-panel__pricing-plan'>
              <XGSelect
                name='name'
                size='large'
                onChange={() => {}}
                onClick={(e) => {
                  onChangePricingPlan(
                    Number(bookingplanid),
                    Number(tripplan?.tripplanid),
                    Number(e.target?.id)
                  );
                }}
                value={tripplan?.title}
                menuItems={tripPricingPlanItems}
                className='booking-panel__pricing-plan__select'
                disabled={isPassengerRemoving}
              />
              <Box className='booking-panel__pricing-plan__passengers'>
                {passengers?.map((passenger, index) => {
                  const { passengerid, fullname } = passenger;
                  return (
                    <Box
                      key={index}
                      className='booking-panel__pricing-plan__passengers__row'
                    >
                      {isPassengerRemoving && (
                        <XGCheckBox
                          id={`${passengerid}`}
                          name={`${passengerid}`}
                          key={`${passengerid}`}
                          data-testid={`${passengerid}`}
                          value={`${passengerid}`}
                          checked={checkedPassengerIds?.includes(
                            Number(passengerid)
                          )}
                          onChange={(e) => {
                            handleChecked(bookingplanid, Number(e.target?.id));
                          }}
                        />
                      )}

                      <span
                        className={`booking-panel__pricing-plan__passenger ${isPassengerRemoving ? 'booking-panel__pricing-plan__passenger--remove' : ''}`}
                        key={index}
                      >
                        {fullname}
                      </span>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            {bookingplans?.length > 1 && index < bookingplans?.length - 1 && (
              <div className='booking-panel__pricing-plan__passenger__divider' />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
};

BookingPricingPlansPanel.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPassengerRemoving: PropTypes.bool,
  data: PropTypes.object,
  setBookingData: PropTypes.func,
  onChangePricingPlan: PropTypes.func,
  checkedPassengers: PropTypes.array,
  tripPricingPlans: PropTypes.array
};

export default BookingPricingPlansPanel;
